import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import type { Member, Professional } from 'generated/graphql';
import type { MeUser } from '~/server/types';
import { getUserInitials } from '~/modules/user';

type MeType = Pick<
    MeUser,
    '__typename' | 'ID' | 'Name' | 'Initials' | 'ProfileImage'
>;
type UserType = Pick<
    Member,
    '__typename' | 'ID' | 'Name' | 'Initials' | 'ProfileImageLink'
>;
type ProfessionalType = Pick<
    Professional,
    '__typename' | 'ID' | 'Title' | 'CompanyLogo' | 'LogoBackgroundColor'
>;
type Participant = MeType | UserType | ProfessionalType;

/**
 * The avatar component for a conversation participant. The participant can be
 * a user (member) or a professional.
 */
export function ParticipantAvatar({
    className,
    imageClassName,
    fallbackClassName,
    size,
    participant,
}: {
    className?: string;
    imageClassName?: string;
    fallbackClassName?: string;
    size?: React.ComponentPropsWithoutRef<typeof Avatar>['size'];
    participant?: Participant;
}) {
    const isProfessional = participant?.__typename === 'Professional';
    const imageSrc = isProfessional
        ? participant.CompanyLogo
        : participant?.__typename === 'Member'
          ? participant.ProfileImageLink
          : participant?.ProfileImage;

    return (
        <Avatar className={className} size={size}>
            <AvatarImage
                className={imageClassName}
                src={imageSrc || undefined}
                alt={isProfessional ? participant.Title : participant?.Name}
                fit={isProfessional ? 'contain' : 'cover'}
                style={
                    isProfessional
                        ? {
                              // Professionals without a logo background color defaults to white.
                              backgroundColor:
                                  participant.LogoBackgroundColor ?? 'white',
                          }
                        : undefined
                }
            />
            <AvatarFallback className={fallbackClassName} delayMs={0}>
                {isProfessional
                    ? participant.Title[0]?.toUpperCase()
                    : participant
                      ? getUserInitials(participant)
                      : ''}
            </AvatarFallback>
        </Avatar>
    );
}
